import { batch } from 'react-redux'

import {
  ACTION_TYPES,
  DIALOGS,
  VIEW_ONLY_CONFIGS,
  DID_STORAGE_NAME,
  JOIN_TYPES,
  CUSTOM_ROOM_VALIDATION_FAILED,
  CALENDAR_EVENT_PROVIDERS
} from '~/core/constants'
import { logError, logInfo } from '~/core/logger'
import { showErrorNotification } from '~/core/ui'
import analytics, { updateDeviceID } from '~/services/analytics'
import {
  hideDialog,
  setDialog,
  setFullScreenState,
  setRoomInputError,
  setJoinButtonDisabled
} from './ui'
import {
  getFullNameFromUrl,
  getConfigsFromUrl,
  isValidCustomRoomName,
  normalizeRoomName
} from '~/core/utils'
import { getPersonalRoom, setCustomRoomName } from './room'
import { fetchValidRoomInfo, fetchMeetingDid } from '~/services/room'
import canJoinService from '~/core/can-join'
import api from '~/services/api'
import {
  getRoomName,
  getIsSessionClosed
} from '~/selectors/active-session'
import { translate } from '~/core/translate'
import { getAuthenticated, getIsElectron, getShouldLaunchUndocked } from '~/selectors/app'
import externalAudio from '~/core/external-audio'
import { callNumber } from '~/services/call-control'
import { getPersonalRoom as selectPersonalRoom } from '~/selectors/room'
import { endMeeting, loadUndockedJitsiMeeting } from './meeting-launch'
import { focusUndockedWindow } from '~/services/jitsi-api'

const { ACTIVE_SESSION } = ACTION_TYPES

const setRoomDetails = details => ({
  type: ACTIVE_SESSION.SET_DETAILS,
  payload: details
})

export const disablePrejoin = () => ({
  type: ACTIVE_SESSION.DISABLE_PREJOIN
})

export const setSessionStatus = payload => ({
  type: ACTIVE_SESSION.SET_SESSION_STATUS,
  payload
})

export const focusMeeting = () => (dispatch, getState) => {
  if (getShouldLaunchUndocked(getState())) {
    focusUndockedWindow()
  } else {
    dispatch(setFullScreenState(true))
  }
}

const joinRoom = ({ details, isDeeplinked = false, viewOnly = false } = {}) => (
  dispatch,
  getState
) => {
  const state = getState()
  const isViewOnly = viewOnly || externalAudio.shouldDialIntoMeeting()
  const canStartNewSession = getIsSessionClosed(state)
  const shouldLaunchUndocked = getShouldLaunchUndocked(state)
  const showConfirmationDialog = !shouldLaunchUndocked && isDeeplinked && canStartNewSession

  const processedDetails = isViewOnly
    ? {
        ...details,
        customRoomConfigs: {
          ...details.customRoomConfigs,
          config: { ...details.customRoomConfigs?.config, ...VIEW_ONLY_CONFIGS.customRoomConfigs.config }
        }
      }
    : details

  logInfo(
    { __filename, details: processedDetails, canStartNewSession },
    'Join room triggered.'
  )

  if (canStartNewSession && !showConfirmationDialog) {
    dispatch(setActiveSession(processedDetails))
    return
  }

  const roomName = getRoomName(state)

  if (roomName === processedDetails.roomName) {
    logInfo({ __filename }, 'Already joined this room. Focusing active meeting')

    dispatch(focusMeeting())

    return
  }

  if (showConfirmationDialog) {
    logInfo(
      { __filename },
      'Opening confirm join meeting dialog'
    )

    dispatch(setDialog(DIALOGS.CONFIRM_JOIN, { details: processedDetails }))
  } else {
    logInfo(
      { __filename },
      'Opening switch meeting confirmation dialog'
    )

    dispatch(setDialog(DIALOGS.SWITCH_MEETING, { details: processedDetails }))
  }
}

export const joinPersonalRoom = ({
  viewOnly = false,
  replaceParticipant = false,
  subject
} = {}) => async (
  dispatch,
  getState
) => {
  logInfo({ __filename }, 'Joining personal room')

  analytics.trackJoinMeeting(analytics.EVENT_STATES.INIT, {
    origin: analytics.JOIN_MEETING_ORIGINS.START,
    startSilent: viewOnly
  })

  let personalRoom = selectPersonalRoom(getState())

  if (!personalRoom.fullName) {
    personalRoom = await dispatch(getPersonalRoom())
  }

  if (personalRoom) {
    const details = { roomName: personalRoom.fullName, personalRoom }
    if (replaceParticipant) {
      details.customRoomConfigs = {
        config: {
          replaceParticipant: true
        }
      }
    }
    if (subject) {
      details.subject = subject
    }

    return dispatch(joinRoom({ details, viewOnly }))
  }

  analytics.trackJoinMeeting(analytics.EVENT_STATES.FAIL, {
    origin: analytics.JOIN_MEETING_ORIGINS.START,
    reason: 'Meeting not found'
  })

  showErrorNotification(translate('could_not_join_personal_room'))

  return false
}

export const joinCustomRoom = ({
  value,
  isDeeplinked = false,
  viewOnly = false,
  replaceParticipant = false,
  subject
}) => async (dispatch, getState) => {
  const customRoomConfigs = getConfigsFromUrl(value)
  if (getIsElectron(getState())) {
    const newDid = customRoomConfigs[DID_STORAGE_NAME]
    if (newDid) {
      updateDeviceID(newDid)
    }
  }

  if (replaceParticipant) {
    customRoomConfigs.config.replaceParticipant = true
  }

  const input = decodeURIComponent(normalizeRoomName(value).trim())

  dispatch(setCustomRoomName(input))
  logInfo({ __filename, input }, 'Joining custom room')

  if (isDeeplinked) {
    analytics.trackJoinMeeting(analytics.EVENT_STATES.INIT, {
      origin: analytics.JOIN_MEETING_ORIGINS.DEEPLINK,
      meetingId: input,
      startSilent: viewOnly
    })
  } else if (getAuthenticated(getState())) {
    analytics.trackJoinMeeting(analytics.EVENT_STATES.INIT, {
      origin: analytics.JOIN_MEETING_ORIGINS.JOIN_BY_ID,
      meetingId: input,
      startSilent: viewOnly
    })
  } else {
    analytics.trackJoinMeeting(analytics.EVENT_STATES.INIT, {
      origin: analytics.JOIN_MEETING_ORIGINS.GUEST_SCREEN,
      meetingId: input,
      startSilent: viewOnly
    })
  }

  if (!isValidCustomRoomName(input)) {
    dispatch(setRoomInputError(true))
    return
  }

  dispatch(setJoinButtonDisabled(true))

  const result = await fetchValidRoomInfo(input)

  if (api.isErrorResponse(result) && result?.type === CUSTOM_ROOM_VALIDATION_FAILED) {
    dispatch(setRoomInputError(true))
  } else {
    const details = { roomName: result?.fqn || input, customRoomConfigs }

    if (!api.isErrorResponse(result)) {
      details.customRoom = result
      if (subject) {
        details.subject = subject
      }
    }

    dispatch(joinRoom({ details, isDeeplinked, viewOnly }))
  }

  dispatch(setJoinButtonDisabled(false))
}

export const joinRoomByUrl = ({
  url,
  viewOnly = false,
  provider,
  ...rest
}) => dispatch => {
  if (provider && provider !== CALENDAR_EVENT_PROVIDERS.EGHT && url) {
    window.open(url, '_blank')
    return
  }

  const roomName = getFullNameFromUrl(url)
  const details = { roomName, ...rest, event: rest.event, url }

  dispatch(joinRoom({ details, viewOnly }))
}

export const joinByType = (joinType, args) => dispatch => {
  let join

  switch (joinType) {
    case JOIN_TYPES.CUSTOM:
      join = joinCustomRoom
      break
    case JOIN_TYPES.PERSONAL:
      join = joinPersonalRoom
      break
    case JOIN_TYPES.URL:
      join = joinRoomByUrl
      break
    default:
      join = joinPersonalRoom
  }

  return dispatch(join(args))
}

export const trackedJoinRoomByUrl = (options, recentId) => dispatch => {
  analytics.trackJoinMeeting(analytics.EVENT_STATES.INIT, {
    origin: analytics.JOIN_MEETING_ORIGINS.RECENTS,
    url: options.url,
    recentId,
    startSilent: false
  })

  return dispatch(joinRoomByUrl(options))
}

export const resetActiveSession = () => dispatch => {
  logInfo({ __filename }, 'Leave room triggered. Resetting state')

  dispatch({
    type: ACTIVE_SESSION.RESET_ACTIVE_SESSION
  })
}

const getStartSilent = details =>
  Boolean(
    details &&
    details.customRoomConfigs &&
    details.customRoomConfigs.config &&
    details.customRoomConfigs.config.startSilent
  )

const getFormattedRoomName = roomName => {
  const isPublicRoom = !roomName.includes('/')
  return isPublicRoom ? `/${roomName}` : roomName
}

export const handleJoinFailed = err => dispatch => {
  logError(
    { __filename, err },
    'Encountered an exception. Failed to join meeting'
  )

  analytics.trackJoinMeeting(analytics.EVENT_STATES.FAIL, {
    reason: err
  })

  batch(() => {
    dispatch(resetActiveSession())
    dispatch(endMeeting())
  })

  showErrorNotification(translate('meeting_service_connection_error_reason'))
}

export const setActiveSession = (details = {}) => async (dispatch, getState) => {
  const startSilent = getStartSilent(details)
  const shouldJoin = await canJoinService.canJoinRoom(startSilent)

  if (!shouldJoin) {
    logInfo({ __filename }, 'Join room prevented')
    return
  }

  hideDialog()
  if (externalAudio.shouldDialIntoMeeting()) {
    const callSetup = externalAudio.getCallSetup()
    try {
      const meetingDid = await fetchMeetingDid(
        getFormattedRoomName(details.roomName)
      )
      await callNumber(callSetup, meetingDid)
    } catch (err) {
      showErrorNotification(translate('failed_join_audio'))
    }
  }

  logInfo({ __filename, details }, 'Join new room triggered.')

  dispatch(setRoomDetails(details))

  const shouldLaunchUndocked = getShouldLaunchUndocked(getState())

  try {
    if (shouldLaunchUndocked) {
      await dispatch(loadUndockedJitsiMeeting())
    }
  } catch (err) {
    dispatch(handleJoinFailed(err))
  }
}
